// SettlementDocTab — Today's settlements by cut-off, release action,
// documentary requirements, cut-off clocks.

import React, { useState, useEffect } from 'react';
import {
  Clock, CheckCircle, AlertTriangle, Lock, FileText, Send, XCircle
} from 'lucide-react';
import { getStatusStyle } from '../../config/statusColors';
import { LP_TRADE_STATUS_LABELS, LP_DOC_TYPE_LABELS } from '../../lib/lp/constants';

const formatCurrency = (val) => {
  if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val?.toLocaleString?.() || '0';
};
const formatRate = (r) => r != null ? Number(r).toFixed(4) : '—';

const getStatusBadge = (status) => {
  const style = getStatusStyle(status);
  return {
    className: 'inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border rounded-none',
    style: { ...style, borderColor: style.color },
  };
};

const CutoffClock = ({ label, cutoff, timezone }) => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const cutoffTime = new Date(cutoff);
  const isPast = now > cutoffTime;
  const diff = cutoffTime - now;
  const hours = Math.floor(Math.abs(diff) / 3600000);
  const mins = Math.floor((Math.abs(diff) % 3600000) / 60000);

  return (
    <div className={`border rounded-none p-3 ${isPast ? 'border-red-200 bg-red-50' : 'border-zinc-200'}`}>
      <div className="flex items-center gap-2 mb-1">
        <Clock className={`w-4 h-4 ${isPast ? 'text-red-600' : 'text-zinc-400'}`} />
        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">{label}</span>
      </div>
      <p className={`font-mono text-lg font-bold ${isPast ? 'text-red-700' : 'text-zinc-800'}`}>
        {cutoffTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: timezone || undefined })}
      </p>
      <p className={`text-[10px] font-mono ${isPast ? 'text-red-600' : 'text-zinc-500'}`}>
        {isPast ? `${hours}h ${mins}m past` : `${hours}h ${mins}m remaining`}
      </p>
    </div>
  );
};

const SettlementDocTab = ({
  trades = [],
  counterparties = {},
  rfqMap = {},
  onReleaseTrade,
  onUpdateSettlement,
}) => {
  const today = new Date().toISOString().slice(0, 10);

  // Filter trades settling today or overdue
  const settlementTrades = trades.filter(t => {
    const vd = t.settlement?.valueDate;
    return vd && vd <= today && !['csd_payment_confirmed', 'cancelled', 'failed'].includes(t.status);
  });

  // Group by currency
  const byCurrency = {};
  settlementTrades.forEach(t => {
    const ccy = t.currencyPair.split('/')[1];
    if (!byCurrency[ccy]) byCurrency[ccy] = [];
    byCurrency[ccy].push(t);
  });

  // Determine doc requirements per trade
  const getRequiredDocs = (trade) => {
    const rfq = rfqMap[trade.rfqId];
    if (!rfq?.requiredDocs) return [];
    const cp = counterparties[trade.counterpartyId];
    const cpDocs = new Set((cp?.documents || []).map(d => d.type));
    return (rfq.requiredDocs || []).map(docType => ({
      type: docType,
      label: LP_DOC_TYPE_LABELS[docType] || docType,
      present: cpDocs.has(docType),
    }));
  };

  const canRelease = (trade) => {
    if (trade.status !== 'funds_received') return false;
    if (trade.preFund && trade.status !== 'funds_received') return false;
    return true;
  };

  return (
    <div className="space-y-6">
      {/* Cut-off Clocks */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <CutoffClock label="KES Cut-off (EAT)" cutoff={`${today}T14:00:00+03:00`} timezone="Africa/Nairobi" />
        <CutoffClock label="UGX Cut-off (EAT)" cutoff={`${today}T13:00:00+03:00`} timezone="Africa/Nairobi" />
        <CutoffClock label="TZS Cut-off (EAT)" cutoff={`${today}T12:00:00+03:00`} timezone="Africa/Nairobi" />
      </div>

      {/* Settlements by Currency */}
      {Object.keys(byCurrency).length === 0 && (
        <div className="bg-white border border-zinc-200 rounded-none p-8 text-center text-zinc-400">
          <CheckCircle className="w-8 h-8 mx-auto mb-2" />
          <p className="text-sm">No settlements due today</p>
        </div>
      )}

      {Object.entries(byCurrency).sort().map(([ccy, ccyTrades]) => (
        <div key={ccy} className="bg-white border border-zinc-200 rounded-none">
          <div className="p-3 border-b border-zinc-100 bg-zinc-50 flex items-center justify-between">
            <span className="font-mono text-xs font-bold">{ccy} Settlements ({ccyTrades.length})</span>
            <span className="font-mono text-[10px] text-zinc-500">
              Total: {formatCurrency(ccyTrades.reduce((s, t) => s + t.counterAmount, 0))}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200">
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Trade</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Pair</th>
                  <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Amount</th>
                  <th className="text-right py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Rate</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Correspondent</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Status</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Docs</th>
                  <th className="text-left py-3 px-3 font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ccyTrades.map(trade => {
                  const docs = getRequiredDocs(trade);
                  const allDocsPresent = docs.every(d => d.present);
                  return (
                    <tr key={trade.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="py-3 px-3">
                        <div className="font-mono text-xs">{trade.id}</div>
                        <div className="font-mono text-[9px] text-zinc-400">{trade.bankDealNo}</div>
                      </td>
                      <td className="py-3 px-3 font-mono text-xs">{trade.currencyPair}</td>
                      <td className="py-3 px-3 text-right font-mono text-xs">{formatCurrency(trade.amount)}</td>
                      <td className="py-3 px-3 text-right font-mono text-xs">{formatRate(trade.rate)}</td>
                      <td className="py-3 px-3 text-xs text-zinc-600">{trade.settlement?.correspondent}</td>
                      <td className="py-3 px-3">
                        <span {...getStatusBadge(trade.status)}>
                          {LP_TRADE_STATUS_LABELS[trade.status] || trade.status}
                        </span>
                        {trade.preFund && (
                          <span className="ml-1 font-mono text-[8px] text-zinc-400">PRE-FUND</span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1">
                          {docs.map(d => (
                            <span
                              key={d.type}
                              title={d.label}
                              className={`w-4 h-4 rounded-none flex items-center justify-center ${
                                d.present ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {d.present ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                            </span>
                          ))}
                          {docs.length === 0 && <span className="text-[10px] text-zinc-400">N/A</span>}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        {canRelease(trade) ? (
                          <button
                            onClick={() => onReleaseTrade(trade.id)}
                            className="flex items-center gap-1 px-3 py-1 bg-zinc-700 text-white text-xs font-mono uppercase rounded-none hover:bg-zinc-800 transition-colors"
                          >
                            <Send className="w-3 h-3" />
                            Release
                          </button>
                        ) : trade.status === 'awaiting_funds' ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => onUpdateSettlement(trade.id, 'funds_received')}
                              className="flex items-center gap-1 px-2 py-1 border border-zinc-200 text-xs font-mono rounded-none hover:bg-zinc-50"
                            >
                              <CheckCircle className="w-3 h-3" />
                              Funds In
                            </button>
                          </div>
                        ) : trade.status === 'released' ? (
                          <button
                            onClick={() => onUpdateSettlement(trade.id, 'settled')}
                            className="flex items-center gap-1 px-2 py-1 border border-zinc-200 text-xs font-mono rounded-none hover:bg-zinc-50"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Mark Settled
                          </button>
                        ) : (
                          <span className="font-mono text-[10px] text-zinc-400 flex items-center gap-1">
                            <Lock className="w-3 h-3" />
                            {trade.status === 'booked' ? 'Confirm first' : 'Pending'}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SettlementDocTab;
